import React, { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import RentalsTable from '../../components/Rental/RentalsTable';
import { useAuth } from '../../contexts/AuthContext';
import { Loader, MainCard } from 'components';
import ScrollX from 'components/ScrollX';
import { Box, Button } from '@mui/material';
import { PlusOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router';
import RentalFilters from '../../components/Rental/RentalFilters';
import {
    getRentals,
    getRentalProperties,
    setRentalFilters,
    resetRentalFilters
} from 'store/actions/Rentals';
import { filterRentals } from 'store/reducers/Rentals';

// No date range applied until the user actually picks one and hits Apply — the
// filter bar starts blank instead of defaulting to "this week through year end".
const EMPTY_DATES = { from: null, to: null };

export const Rentals = () => {
    const dispatch = useDispatch();
    const { rentals, loading, propertyOptions, filters } = useSelector(state => state.rentals);
    const { user } = useAuth();
    const navigate = useNavigate();

    // A rental manager scoped to specific properties only ever sees those — passed down
    // so the saga can apply it at fetch time (RentalService.getRentalsByOrgId's own rule).
    const managedPropertyIds = user?.orgs?.[user?.active_org_id]?.managed_property_ids;

    useEffect(() => {
        if (user?.active_org_id) {
            dispatch(getRentals(user.active_org_id, managedPropertyIds));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user?.active_org_id, managedPropertyIds, dispatch]);

    useEffect(() => {
        if (user?.active_org_id) {
            dispatch(getRentalProperties(user.active_org_id, managedPropertyIds));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user?.active_org_id, managedPropertyIds, dispatch]);

    const filteredRentals = useMemo(() => filterRentals(rentals, filters), [rentals, filters]);

    const handleRowClick = rental => {
        navigate(`/rentals/${rental.rental_id}`);
    };

    return (
        <MainCard
            title="Reservations"
            content={false}
            secondary={
                <Button
                    variant="contained"
                    sx={{
                        borderRadius: '8px',
                        bgcolor: '#33C78F',
                        textTransform: 'none',
                        boxShadow: 'none',
                        '&:hover': {
                            bgcolor: '#28b07d',
                            boxShadow: 'none'
                        }
                    }}
                    startIcon={<PlusOutlined />}
                    onClick={() => navigate('/rentals/new-rental')}
                    size="small"
                >
                    New Reservation
                </Button>
            }
        >
            {loading && <Loader />}
            <Box sx={{ width: '100%' }}>
                <RentalFilters
                    onSearch={changed => dispatch(setRentalFilters(changed))}
                    onReset={() => dispatch(resetRentalFilters())}
                    defaultDates={EMPTY_DATES}
                    propertyOptions={propertyOptions}
                />
                <ScrollX>
                    <RentalsTable
                        rentals={filteredRentals}
                        onRowClick={handleRowClick}
                        loading={loading}
                    />
                </ScrollX>
            </Box>
        </MainCard>
    );
};
